import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { createPortal } from 'react-dom';

import { ToastViewportProps } from './ToastViewport.props';
import { viewportSx } from './ToastViewport.styles';
import {
  dismissToast,
  getSnapshot,
  removeToast,
  subscribe,
  TOAST_VISIBLE_LIMIT,
} from '../../../utils/toast';
import { STACK_GAP_PX, STACK_PEEK_PX, Toast } from '../../molecules/Toast';

/** Used until a card has reported its real height, on its very first frame. */
const ASSUMED_CARD_HEIGHT_PX = 64;

/**
 * The single place toasts render.
 *
 * Portalled into `document.body` rather than rendered in the React tree, for
 * two concrete reasons: MUI `Dialog` portals to body and would otherwise paint
 * over the stack, and `MainLayout` applies `zoom: 1.1` to its outlet wrapper,
 * which breaks `position: fixed` for anything inside it.
 *
 * Announcement lives on the cards, not here — each is its own live region via
 * `role="status"`/`role="alert"`, so an error interrupts and a confirmation
 * waits. A live region on this container as well would announce twice.
 */
export const ToastViewport: React.FC<ToastViewportProps> = ({ placement: placementProp }) => {
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down('md'));
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const placement = placementProp ?? (isCompact ? 'top' : 'bottom');

  const toasts = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const [heights, setHeights] = useState<Record<number, number>>({});
  const [isReading, setIsReading] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // MUI's modal manager marks every `document.body` child `aria-hidden` while a
  // dialog is open — verified in `ModalManager.ariaHiddenSiblings`, which
  // excludes only the modal's own nodes and has no `aria-live` exemption. That
  // would silence the toast stack exactly when this app raises most of its
  // toasts: saving from inside a dialog. Undo the attribute whenever it lands.
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;

    const unhide = () => {
      if (node.hasAttribute('aria-hidden')) node.removeAttribute('aria-hidden');
    };
    unhide();

    const observer = new MutationObserver(unhide);
    observer.observe(node, { attributes: true, attributeFilter: ['aria-hidden'] });
    return () => observer.disconnect();
  }, []);

  const visible = useMemo(() => toasts.slice(-TOAST_VISIBLE_LIMIT), [toasts]);

  // Fanned out while the user is reading it. Always fanned on touch, because
  // the receding stack is expanded by hover and there is no hover — a stack you
  // cannot open is just hidden information. Also always fanned under reduced
  // motion, where the recede-and-expand gesture doesn't run at all.
  const expanded = isReading || isCompact || prefersReducedMotion || visible.length <= 1;

  const handleMeasure = useCallback((id: number, height: number) => {
    setHeights((previous) => (previous[id] === height ? previous : { ...previous, [id]: height }));
  }, []);

  // Drop measurements for toasts that have gone, so the map can't grow across
  // a long session.
  useEffect(() => {
    const liveIds = new Set(toasts.map((entry) => entry.id));
    setHeights((previous) => {
      const kept = Object.entries(previous).filter(([id]) => liveIds.has(Number(id)));
      return kept.length === Object.keys(previous).length ? previous : Object.fromEntries(kept);
    });
  }, [toasts]);

  // Depth 0 is the front card — the newest, nearest the anchored edge.
  const heightAtDepth = (depth: number): number => {
    const toast = visible[visible.length - 1 - depth];
    return (toast && heights[toast.id]) || ASSUMED_CARD_HEIGHT_PX;
  };

  const offsetAtDepth = (depth: number): number => {
    // Collapsed, the peek is measured between the cards' *leading* edges, not
    // their anchored ones — otherwise a one-line card behind a two-line card
    // peeks by a different amount than the reverse, and the stack looks
    // accidental.
    if (!expanded) {
      return heightAtDepth(0) - heightAtDepth(depth) + depth * STACK_PEEK_PX;
    }
    let offset = 0;
    for (let ahead = 0; ahead < depth; ahead += 1) {
      offset += heightAtDepth(ahead) + STACK_GAP_PX;
    }
    return offset;
  };

  const lastDepth = visible.length - 1;
  const containerHeight = !visible.length
    ? 0
    : expanded
      ? offsetAtDepth(lastDepth) + heightAtDepth(lastDepth)
      : heightAtDepth(0) + lastDepth * STACK_PEEK_PX;

  return createPortal(
    <Box
      ref={containerRef}
      role="region"
      aria-label="Notifications"
      data-testid="toast-viewport"
      sx={viewportSx(placement, containerHeight)}
      onMouseEnter={() => setIsReading(true)}
      onMouseLeave={() => setIsReading(false)}
      onFocus={() => setIsReading(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setIsReading(false);
      }}
    >
      {visible.map((toast, index) => {
        const depth = visible.length - 1 - index;
        return (
          <Toast
            key={toast.id}
            toast={toast}
            depth={depth}
            offset={offsetAtDepth(depth)}
            paused={isReading}
            expanded={expanded}
            placement={placement}
            onMeasure={handleMeasure}
            onDismiss={dismissToast}
            onRemove={removeToast}
          />
        );
      })}
    </Box>,
    document.body
  );
};
